import { SecurityTabs } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityTabs/SecurityTabs.tsx';
import {
	SecurityDetailsControllerContext,
	useSecurityDetailsController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/useSecurityDetailsController.ts';
import { useSecuritiesController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/useSecuritiesController.tsx';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import {
	CapMenu,
	StyledCapMenuItem,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CapMenu.tsx';
import { DateSelectionMenu } from '@dealstack/frontend/app/pages/Project/Cap/common/DateSelectMenu/dateSelectMenu.tsx';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { SecurityDeleteConfirmationDrawer } from '@dealstack/frontend/shared/components/SecurityDeleteConfirmationDrawer/SecurityDeleteConfirmationDrawer.tsx';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors.ts';
import { DataSecurityTct } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Flex, Text } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

interface Props {
	selectedEntityLoading?: boolean;
}

export const SecurityDetails = ({ selectedEntityLoading }: Props) => {
	const tCapManage = useTranslationCapManage();
	const navigate = useNavigate();
	const { securityId } = useParams();

	const securityDetailsController = useSecurityDetailsController();
	const { getSecurityDetailsRequest, hasEditorPermission, filterDate, setFilterDate } =
		securityDetailsController;

	const [confirmDeleteOpened, setConfirmDeleteOpened] = useState<boolean>(false);
	const [securityToDelete, setSecurityToDelete] = useState<DataSecurityTct>();

	useEffect(() => {
		if (securityId) {
			securityDetailsController.refetchSecurityDetails(securityId);
		}
	}, [securityId, filterDate]);

	const data = getSecurityDetailsRequest.data;
	const isLoading = getSecurityDetailsRequest.isPending || selectedEntityLoading;
	const {
		fetchTsaData,
		isTsaExist,
		onDelete,
		deleteSecurityRequest,
		userIsAdmin,
		selectedEntityName,
		selectedEntityId,
		dealId,
	} = useSecuritiesController();

	const breadcrumbs = useMemo(
		() =>
			DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.SECURITY_DETAILS]({
				tCapManage,
				dealId,
				personId: selectedEntityId,
				personDisplayName: selectedEntityName,
				dataTestId: TEST_ELEMENT_IDS.CAP_BACK_TO_SECURITIES_BUTTON,
			}),
		[tCapManage, dealId, selectedEntityId, selectedEntityName]
	);

	useBreadcrumbs(breadcrumbs);

	const disableDelete = !!data?.exchangedFrom?.length;
	const deleteAlertLabels = (): { title: string; description: string } => {
		if (disableDelete) {
			return {
				title: tCapManage('delete.security.drawer.single.exchangedForDeleteAlertTitle'),
				description: tCapManage(
					'delete.security.drawer.single.exchangedForDeleteAlertDescription',
					{
						securityNames: data?.exchangedFrom
							?.map((sec) => sec?.security?.name)
							?.join(', '),
					}
				),
			};
		}

		if (isTsaExist) {
			return {
				title: tCapManage('delete.security.drawer.single.forbiddenTitle'),
				description: tCapManage('delete.security.drawer.single.forbiddenDescription'),
			};
		}

		return {
			title: tCapManage('delete.security.drawer.single.alertTitle'),
			description: tCapManage('delete.security.drawer.single.alertDescription'),
		};
	};

	const handleDeleteClick = useCallback(
		async (security?: DataSecurityTct) => {
			if (!security) {
				return;
			}

			setSecurityToDelete(security);
			await fetchTsaData([security.id]);

			setConfirmDeleteOpened(true);
		},
		[fetchTsaData]
	);

	const navigateToSecurities = () => navigate(`..${PEOPLE_ROUTES.SECURITIES}`);

	const handleConfirmDelete = useCallback(async () => {
		if (securityToDelete?.id) {
			onDelete(securityToDelete.id);
			deleteSecurityRequest.mutate(securityToDelete.id, {
				onSuccess: navigateToSecurities,
				onError: (error: { error?: { code?: number; message?: string } }) => {
					if (
						error?.error?.code &&
						error.error?.message &&
						error.error.code === ERROR_CODES.PROJECT.CAP.SECURITIES[120314]
					) {
						showToastV2({
							message: tCapManage(
								'toasts.error.security_cannot_be_deleted.exchanged_for_error',
								{
									securityName: error.error.message,
								}
							),
							state: 'error',
						});
					}
				},
			});
		}
	}, [securityToDelete, deleteSecurityRequest, onDelete, navigate]);

	return (
		<PageAbsoluteContainer data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_PAGE_CONTAINER}>
			{isLoading && <InlineLoader />}
			<SecurityDetailsControllerContext.Provider value={securityDetailsController}>
				<PageHeader
					pageTitle={
						getSecurityDetailsRequest?.data?.name ??
						tCapManage('securities.details.title')
					}
				>
					<Flex justify="flex-end" align="center" gap="0.5rem">
						<DateSelectionMenu
							key="dateSelectionMenu"
							dateTo={filterDate}
							setFilterDate={setFilterDate}
							allowDeselect={true}
							defaultDate={filterDate ?? undefined}
							buttonDefaultLabel={tCapManage('date.selection.today', {
								defaultValue: 'Today',
							})}
						/>

						<Link to={`..${PEOPLE_ROUTES.SECURITY_RANKING}`}>
							<Button
								data-testid={TEST_ELEMENT_IDS.CAP_ADD_MANAGE_RANKING_BUTTON}
								variant="default"
								leftSection={<Icon variant="ranking" size="lg" />}
							>
								{tCapManage('securities.header_actions.manage_seniority')}
							</Button>
						</Link>

						{userIsAdmin && (
							<CapMenu buttonTargetIcon={<Icon variant="more-v3" size="vlg" />}>
								<StyledCapMenuItem
									p="0.5rem"
									disabled={deleteSecurityRequest.isPending}
									onClick={() => handleDeleteClick(data)}
									leftSection={<Icon variant="trash-can" size="md" />}
									color="red.6"
								>
									<Text size="B6Medium">
										{tCapManage('securities.details.security_delete')}
									</Text>
								</StyledCapMenuItem>
							</CapMenu>
						)}
					</Flex>
				</PageHeader>

				<Flex pb="1rem" style={{ overflow: 'hidden' }}>
					<SecurityTabs hasEditorPermission={hasEditorPermission} />
				</Flex>

				<SecurityDeleteConfirmationDrawer
					opened={confirmDeleteOpened}
					onClose={() => setConfirmDeleteOpened(false)}
					alertTitle={deleteAlertLabels().title}
					alertDescription={deleteAlertLabels().description}
					securityName={securityToDelete?.name}
					isSuccess={deleteSecurityRequest.isSuccess}
					isLoading={deleteSecurityRequest.isPending}
					onConfirm={handleConfirmDelete}
					disableDeleteButton={disableDelete}
				/>
			</SecurityDetailsControllerContext.Provider>
		</PageAbsoluteContainer>
	);
};
